import { WorkPage, WorkSection } from '../../components/work-page.tsx';
import '../portal-page.css';
import { TableScroll } from '@web/components/table-scroll.tsx';
import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, AdminApiError, type AdminDiscount } from '../../api/admin.ts';
import { Alert, Button, LoadingBlock, Select, TextInput } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { formatCents } from '@shared/calculations/pricing.ts';

/**
 * The discount catalog: Stripe coupons the operators create, optionally with
 * a customer-redeemable code. Granting one to a specific company happens on
 * that company's detail page.
 */

export function discountAmountLabel(discount: AdminDiscount): string {
  const amount =
    discount.percentOff !== null
      ? `${discount.percentOff}% off`
      : `${formatCents(discount.amountOffCents ?? 0, discount.currency ?? 'USD')} off`;
  const duration =
    discount.duration === 'forever'
      ? 'forever'
      : discount.duration === 'once'
        ? 'once'
        : `for ${discount.durationInMonths} months`;
  return `${amount}, ${duration}`;
}

function formatExpiry(expiresAt: string | null): string {
  if (!expiresAt) return '—';
  return new Date(expiresAt).toLocaleDateString(undefined, { timeZone: 'America/New_York' });
}

function formatUses(discount: AdminDiscount): string {
  if (discount.maxRedemptions === null && discount.timesRedeemed === null) return '—';
  const used = discount.timesRedeemed;
  if (used === null) {
    return discount.maxRedemptions === null ? '—' : `— / ${discount.maxRedemptions}`;
  }
  if (discount.maxRedemptions === null) return String(used);
  return `${used} / ${discount.maxRedemptions}`;
}

/** Whether the customer code could still be redeemed (matches signup validation). */
export function isDiscountCodeRedeemable(discount: AdminDiscount, nowMs = Date.now()): boolean {
  if (!discount.code || discount.archivedAt || discount.deactivatedAt) return false;
  if (discount.expiresAt && Date.parse(discount.expiresAt) <= nowMs) return false;
  if (
    discount.maxRedemptions !== null &&
    discount.timesRedeemed !== null &&
    discount.timesRedeemed >= discount.maxRedemptions
  ) {
    return false;
  }
  return true;
}

export function codeStatusLabel(discount: AdminDiscount, nowMs = Date.now()): string | null {
  if (!discount.code) return null;
  if (discount.archivedAt) return 'Archived';
  if (discount.deactivatedAt) return 'Inactive';
  if (!isDiscountCodeRedeemable(discount, nowMs)) return 'Inactive';
  return 'Active';
}

/** Parses max-uses input from the create form; rejects truncated decimals/scientific notation. */
export function parseOptionalMaxUses(raw: string): number | null | 'invalid' {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) return 'invalid';
  const parsed = Number.parseInt(trimmed, 10);
  if (parsed < 1) return 'invalid';
  return parsed;
}

export default function AdminDiscountsPage() {
  useDocumentTitle('Discounts');
  const queryClient = useQueryClient();
  const discounts = useQuery({
    queryKey: ['admin', 'discounts'],
    queryFn: ({ signal }) => adminApi.listDiscounts(signal),
  });

  const [name, setName] = useState('');
  const [percent, setPercent] = useState('20');
  const [duration, setDuration] = useState('forever');
  const [months, setMonths] = useState('3');
  const [code, setCode] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function validateForm(): Record<string, string> {
    const errors: Record<string, string> = {};
    if (maxUses.trim() && parseOptionalMaxUses(maxUses) === 'invalid') {
      errors.maxUses = 'Enter a whole number of at least 1.';
    }
    if (expiresOn.trim()) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(expiresOn.trim())) {
        errors.expiresOn = 'Enter a valid date.';
      } else {
        const end = new Date(`${expiresOn.trim()}T23:59:59`);
        if (end.getTime() < Date.now()) {
          errors.expiresOn = 'The expiry date must be today or later.';
        }
      }
    }
    return errors;
  }

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    const clientErrors = validateForm();
    setFieldErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const percentOff = Number.parseInt(percent, 10);
      const parsedMaxUses = parseOptionalMaxUses(maxUses);
      const maxRedemptions = parsedMaxUses === null || parsedMaxUses === 'invalid' ? undefined : parsedMaxUses;
      await adminApi.createDiscount({
        name: name.trim(),
        percentOff,
        duration: duration as 'once' | 'repeating' | 'forever',
        ...(duration === 'repeating' ? { durationInMonths: Number.parseInt(months, 10) || 3 } : {}),
        ...(code.trim() ? { code: code.trim() } : {}),
        ...(maxRedemptions !== undefined ? { maxRedemptions } : {}),
        ...(expiresOn.trim() ? { expiresOn: expiresOn.trim() } : {}),
      });
      setNotice(
        code.trim() ? `Discount created — customers can use code ${code.trim().toUpperCase()}.` : 'Discount created.',
      );
      setName('');
      setCode('');
      setMaxUses('');
      setExpiresOn('');
      setFieldErrors({});
      await queryClient.invalidateQueries({ queryKey: ['admin', 'discounts'] });
    } catch (caught) {
      if (caught instanceof AdminApiError) {
        setError(caught.message);
        if (Object.keys(caught.fields).length > 0) setFieldErrors(caught.fields);
      } else {
        setError('The discount could not be created.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleDeactivate(discount: AdminDiscount) {
    if (
      !window.confirm(
        `Deactivate code "${discount.code ?? discount.name}"? Customers can no longer redeem it at checkout.`,
      )
    ) {
      return;
    }
    setError(null);
    try {
      await adminApi.deactivateDiscount(discount.id);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'discounts'] });
    } catch (caught) {
      setError(caught instanceof AdminApiError ? caught.message : 'The code could not be deactivated.');
    }
  }

  async function handleArchive(discount: AdminDiscount) {
    if (
      !window.confirm(
        `Archive "${discount.name}"? Companies already discounted keep it; it can no longer be granted or redeemed.`,
      )
    ) {
      return;
    }
    setError(null);
    try {
      await adminApi.archiveDiscount(discount.id);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'discounts'] });
    } catch (caught) {
      setError(caught instanceof AdminApiError ? caught.message : 'The discount could not be archived.');
    }
  }

  return (
    <div className="portal-page">
      <WorkPage
        title="Discounts"
        description="Percent-off coupons on the deployment's Stripe account. Add a code to let customers redeem it at checkout; grant one directly from a company's page."
      >
        {error ? (
          <div className="mb-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}
        {notice ? (
          <div className="mb-4">
            <Alert tone="success">{notice}</Alert>
          </div>
        ) : null}

        <WorkSection title="Create a discount">
          <form onSubmit={handleCreate} className="flex flex-wrap items-start gap-3" noValidate>
            <div className="min-w-48 flex-1">
              <TextInput
                label="Name"
                required
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Founders deal"
              />
            </div>
            <div className="w-28">
              <TextInput
                label="Percent off"
                type="number"
                min={1}
                max={100}
                required
                value={percent}
                onChange={(event) => setPercent(event.target.value)}
              />
            </div>
            <div className="w-36">
              <Select
                label="Duration"
                options={[
                  { value: 'forever', label: 'Forever' },
                  { value: 'repeating', label: 'For N months' },
                  { value: 'once', label: 'First charge only' },
                ]}
                value={duration}
                onChange={(event) => setDuration(event.target.value)}
              />
            </div>
            {duration === 'repeating' ? (
              <div className="w-24">
                <TextInput
                  label="Months"
                  type="number"
                  min={1}
                  max={36}
                  value={months}
                  onChange={(event) => setMonths(event.target.value)}
                />
              </div>
            ) : null}
            <div className="w-44">
              <TextInput
                label="Code (optional)"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="SPRING20"
                hint="Redeemable at checkout"
              />
            </div>
            <div className="w-28">
              <TextInput
                label="Max uses (optional)"
                type="number"
                min={1}
                value={maxUses}
                onChange={(event) => setMaxUses(event.target.value)}
                error={fieldErrors.maxUses}
              />
            </div>
            <div className="w-40">
              <TextInput
                label="Expires on (optional)"
                type="date"
                value={expiresOn}
                onChange={(event) => setExpiresOn(event.target.value)}
                error={fieldErrors.expiresOn}
                hint="End of day, Eastern time"
              />
            </div>
            <div className="mb-4">
              {/* Invisible label lines the button up with the inputs rather than with the code field's hint. */}
              <span className="field-label invisible" aria-hidden="true">
                Create discount
              </span>
              <Button type="submit" variant="primary" busy={busy} disabled={!name.trim() || !percent}>
                Create discount
              </Button>
            </div>
          </form>
        </WorkSection>

        {discounts.isLoading ? (
          <LoadingBlock label="Loading discounts…" />
        ) : discounts.isError ? (
          <Alert tone="error" title="The discounts could not be loaded">
            <Button variant="ghost" onClick={() => void discounts.refetch()}>
              Try again
            </Button>
          </Alert>
        ) : (discounts.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-[color:var(--color-muted)]">No discounts yet.</p>
        ) : (
          <TableScroll aria-label="Discounts" className="card overflow-x-auto p-0">
            {/* The wrapper scrolls only if the table may outgrow it; `w-full`
              alone pins it and the columns break mid-word instead. */}
            <table className="data-table w-full min-w-max text-sm">
              <thead>
                <tr className="border-b border-[color:var(--color-line)] text-left text-[color:var(--color-muted)]">
                  <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                    Name
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                    Amount
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                    Code
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                    Uses
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                    Expires
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                    Created
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium" />
                </tr>
              </thead>
              <tbody>
                {(discounts.data ?? []).map((discount) => {
                  const status = codeStatusLabel(discount);
                  return (
                    <tr key={discount.id} className="border-b border-[color:var(--color-line)] last:border-b-0">
                      <td className="max-w-[18rem] table-text px-4 py-3 font-medium">{discount.name}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{discountAmountLabel(discount)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {discount.code ? (
                          <span className="inline-flex items-center gap-2">
                            <code className="rounded bg-[color:var(--color-surface)] px-1.5 py-0.5">
                              {discount.code}
                            </code>
                            {status ? <span className="text-xs text-[color:var(--color-muted)]">{status}</span> : null}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{formatUses(discount)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-[color:var(--color-muted)]">
                        {formatExpiry(discount.expiresAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-[color:var(--color-muted)]">
                        {new Date(discount.createdAt).toLocaleDateString()} by {discount.createdBy}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="flex justify-end gap-1">
                          {discount.code && isDiscountCodeRedeemable(discount) ? (
                            <Button variant="ghost" onClick={() => void handleDeactivate(discount)}>
                              Deactivate
                            </Button>
                          ) : null}
                          <Button variant="ghost" onClick={() => void handleArchive(discount)}>
                            Archive
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        )}
      </WorkPage>
    </div>
  );
}
